/**
 * HITL review field helpers for AI Lead Intake.
 * Load type uses canonical engine values: shared_load | exclusive_load.
 */

import {
  normalizeServiceType,
  SERVICE_TYPE,
} from "../../../shared/utils/serviceTypeLanguage.js";
import {
  hasInventoryContent,
  parseInventoryRowsFromFields,
  serializeInventoryRowsToItems,
  serializeInventoryRowsToItemsText,
} from "./structuredInventoryHelpers.js";

export const LOAD_TYPE_OPTIONS = Object.freeze([
  { value: SERVICE_TYPE.SHARED_LOAD, label: "Shared load" },
  { value: SERVICE_TYPE.EXCLUSIVE_LOAD, label: "Exclusive load" },
]);

/**
 * Prefer one inventory line per row for editing.
 * Comma-separated single-line pastes become newline-separated.
 * @param {unknown} text
 * @returns {string}
 */
export function formatItemsTextForDisplay(text) {
  const raw = String(text ?? "");
  if (!raw.trim()) return "";
  if (raw.includes("\n")) return raw;
  if (raw.includes(",")) {
    return raw
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .join("\n");
  }
  return raw;
}

/**
 * Resolve editor text from formPrefill / draft fields (string or items array).
 * @param {{ items?: unknown, itemsText?: unknown, itemsArray?: unknown }} [input]
 * @returns {string}
 */
export function resolveItemsEditorText(input = {}) {
  const { items, itemsText, itemsArray } = input;
  if (typeof items === "string" && items.trim()) {
    return formatItemsTextForDisplay(items);
  }
  if (typeof itemsText === "string" && itemsText.trim()) {
    return formatItemsTextForDisplay(itemsText);
  }
  const list = Array.isArray(itemsArray)
    ? itemsArray
    : Array.isArray(items)
      ? items
      : null;
  if (list?.length) {
    return list
      .map((entry) =>
        typeof entry === "string"
          ? entry.trim()
          : String(entry?.content || entry?.description || "").trim()
      )
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

/**
 * Split free-text inventory into lead item contents (newlines and commas).
 * @param {unknown} text
 * @returns {string[]}
 */
export function parseItemsTextLines(text) {
  return String(text ?? "")
    .split(/[\n,]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Canonical load/service type from HITL / draft fields.
 * @param {unknown} value
 * @param {object} [fields]
 * @returns {""|"shared_load"|"exclusive_load"}
 */
export function resolveLoadTypeValue(value, fields = {}) {
  const normalized = normalizeServiceType(
    value ||
      fields.serviceType ||
      fields.loadType ||
      fields.preferredLoadType ||
      fields.qualification?.serviceType
  );
  return normalized || "";
}

/**
 * Build fields payload for Save / Accept (engine-compatible serviceType).
 * @param {object} fields — HITL form state
 * @returns {object}
 */
export function buildHitlSaveFields(fields = {}) {
  const serviceType = resolveLoadTypeValue(fields.serviceType, fields);
  const inventoryRows = parseInventoryRowsFromFields({
    inventoryRows: fields.inventoryRows,
    itemsText: fields.itemsText,
    items: fields.items,
  });
  const structuredItems = serializeInventoryRowsToItems(inventoryRows);
  const itemsText =
    serializeInventoryRowsToItemsText(inventoryRows) ||
    fields.itemsText ||
    "";
  // Prefer structured objects for Accept; keep string lines as fallback for older drafts
  const itemLines =
    structuredItems.length > 0
      ? structuredItems
      : parseItemsTextLines(fields.itemsText);
  const existingQualification =
    fields.qualification && typeof fields.qualification === "object"
      ? { ...fields.qualification }
      : {};

  const distanceKm = parsePositiveDistanceKm(
    fields.distance_km ?? fields.routeKm ?? existingQualification.distance?.km
  );
  const qualification = {
    ...existingQualification,
    ...(serviceType ? { serviceType } : {}),
  };
  if (distanceKm != null) {
    qualification.distance = {
      ...(existingQualification.distance || {}),
      km: distanceKm,
      min: distanceKm,
      max: distanceKm,
      calculatedAt: new Date().toISOString(),
      source: "hitl_manual",
    };
  }

  const elementConfirmations = Array.isArray(fields.elementConfirmations)
    ? fields.elementConfirmations
    : existingQualification.elementConfirmations;

  return {
    ...fields,
    move_dates: fields.move_date ? [fields.move_date] : [],
    inventoryRows,
    itemsText,
    items: itemLines,
    serviceType: serviceType || null,
    distance_km: distanceKm != null ? String(distanceKm) : fields.distance_km || "",
    elementConfirmations: elementConfirmations || [],
    qualification: {
      ...qualification,
      ...(Array.isArray(elementConfirmations)
        ? { elementConfirmations }
        : {}),
    },
  };
}

export { hasInventoryContent };

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function parsePositiveDistanceKm(value) {
  if (value == null || value === "") return null;
  const n = Number(String(value).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}
